import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  PinInput,
  PinInputField,
  Text,
  useColorModeValue,
  FormErrorMessage,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { Field, Form, Formik } from 'formik';
import DefaultAuth from "layouts/auth/Default";
import illustration from "assets/img/auth/auth.jpg";
import { useAuth } from '../../../authProvider';
import routes from 'routes.js';

function ValidateOTP() {
  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = "gray.400";
  const textColorBrand = useColorModeValue("brand.500", "white");
  const brandStars = useColorModeValue("brand.500", "brand.400");
  const [otpError, setOtpError] = useState("");
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [countdown, setCountdown] = useState(300);
  const history = useHistory();
  const { sendOTP, resendOTP, signOut } = useAuth();


  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prevCount) => {
          if (prevCount <= 1) {
            setIsResendDisabled(false);
            clearInterval(timer);
            return 0;
          }
          return prevCount - 1;
        });
      }, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [countdown]);

  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  function validateOTP(value) {
    let error;
    if (!value) {
      error = 'OTP is required';
    } else if (value.length !== 6) {
      error = 'OTP must be 6 digits';
    }
    return error;
  }

  // Handle resend OTP
  const handleResendOTP = async () => {
    try {
      await resendOTP();
      setCountdown(300);
      setIsResendDisabled(true);
      setOtpError(""); // Clear any existing errors
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to resend OTP";
      setOtpError(errorMessage);
      console.error('Resend OTP error:', error);
    }
  };

  const getPathByName = (name) => {
    const route = routes.find(route => route.name.toLowerCase() === name.toLowerCase());
    return route ? route.path : null; // Return the path or null if not found
  };

  // Handle OTP verification
  const handleVerifyOTP = async (values, actions) => {
    try {
      // define the data to be sent to the server
      const otpData = {
        email: "",
        otp: values.otp
      };
      await sendOTP(otpData);
      setOtpError(""); // Clear any existing errors
      let rawList = JSON.parse(localStorage.getItem('verified_user'))
      let menuList = rawList ? rawList.roleMenu : null
      history.push(`/admin${getPathByName(menuList[0]?.menu_name)}`);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to verify OTP";
      setOtpError(errorMessage);
      console.error('OTP verification error:', error);
      actions.setSubmitting(false);
    }
  };

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        maxW={{ base: "100%", md: "max-content" }}
        w='100%'
        mx={{ base: "auto", lg: "0px" }}
        me='auto'
        h='100%'
        alignItems='start'
        justifyContent='center'
        mb={{ base: "30px", md: "60px" }}
        px={{ base: "25px", md: "0px" }}
        mt={{ base: "40px", md: "14vh" }}
        flexDirection='column'>
        <Box me='auto'>
          <Heading color={textColor} fontSize='36px' mb='10px'>
            Enter OTP
          </Heading>
          <Text
            mb='36px'
            ms='4px'
            color={textColorSecondary}
            fontWeight='400'
            fontSize='md'>
            Enter the verification code sent to your email
          </Text>
        </Box>
        {otpError && ( // Added error display
          <Alert status="error" mb={4}>
            <AlertIcon />
            {otpError}
          </Alert>
        )}
        <Flex
          zIndex='2'
          direction='column'
          w={{ base: "100%", md: "420px" }}
          maxW='100%'
          background='transparent'
          borderRadius='15px'
          mx={{ base: "auto", lg: "unset" }}
          me='auto'
          mb={{ base: "20px", md: "auto" }}>

          <Formik
            initialValues={{ otp: "" }}
            onSubmit={handleVerifyOTP}
          >
            {(props) => (
              <Form>
                <Field name="otp" validate={validateOTP}>
                  {({ field, form }) => (
                    <FormControl isInvalid={form.errors.otp && form.touched.otp}>
                      <FormLabel
                        htmlFor="otp"
                        display='flex'
                        ms='4px'
                        fontSize='sm'
                        fontWeight='500'
                        color={textColor}
                        mb='8px'
                      >
                        OTP Code<Text color={brandStars}>*</Text>
                      </FormLabel>
                      <HStack spacing='4' justify='center' mb='24px'>
                        <PinInput
                          size='lg'
                          placeholder="_"
                          otp
                          {...field}
                          onChange={(value) => {
                            form.setFieldValue('otp', value);
                          }}
                        >
                          <PinInputField borderColor='gray.200' _focus={{ borderColor: 'brand.500' }} />
                          <PinInputField borderColor='gray.200' _focus={{ borderColor: 'brand.500' }} />
                          <PinInputField borderColor='gray.200' _focus={{ borderColor: 'brand.500' }} />
                          <PinInputField borderColor='gray.200' _focus={{ borderColor: 'brand.500' }} />
                          <PinInputField borderColor='gray.200' _focus={{ borderColor: 'brand.500' }} />
                          <PinInputField borderColor='gray.200' _focus={{ borderColor: 'brand.500' }} />
                        </PinInput>
                      </HStack>
                      <FormErrorMessage textAlign='center'>{form.errors.otp}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                <Text
                  mb='16px'
                  ms='4px'
                  color={textColorSecondary}
                  fontSize='sm'
                  textAlign='center'
                >
                  Time remaining: {formatTime(countdown)}
                </Text>

                <Button
                  fontSize='sm'
                  variant='brand'
                  fontWeight='500'
                  w='100%'
                  h='50'
                  mb='16px'
                  isLoading={props.isSubmitting}
                  type="submit"
                >
                  Verify OTP
                </Button>

                <HStack spacing={4} justify="center" mb='24px'>
                  <Button
                    fontSize='sm'
                    variant='ghost'
                    fontWeight='500'
                    h='50'
                    onClick={async () => {
                      try {
                        await signOut();
                        history.push('/auth/sign-in');
                      } catch (error) {
                        console.error('Logout failed:', error);
                        // Optionally add error handling UI here
                      }
                    }}
                  >
                    Back to Login
                  </Button>
                  <Button
                    fontSize='sm'
                    variant='ghost'
                    fontWeight='500'
                    h='50'
                    isDisabled={isResendDisabled}
                    onClick={handleResendOTP}
                    color={textColorBrand}
                    _hover={{
                      bg: 'transparent',
                      color: textColorBrand,
                      opacity: 0.8
                    }}
                  >
                    Resend Code
                  </Button>
                </HStack>
              </Form>
            )}
          </Formik>
        </Flex>
      </Flex>
    </DefaultAuth>
  );
}

export default ValidateOTP;
